/**
 * useAnomalyToasts — Phase G · rising-edge anomaly notifications.
 *
 * Diff-based watcher over the `anomalyReasons` array exposed by
 * `usePipelineStats`. Whenever a NEW reason code transitions from
 * "absent" → "present", we push a global Sonner toast so the admin
 * is alerted immediately without having to mouse over the sidebar.
 *
 * Diff direction:
 *   • absent → present  →  rose / error toast  ("Pipeline anomaly detected")
 *   • present → absent  →  emerald / success toast  ("Pipeline recovered")
 *
 * Why rising-edge and NOT a fixed-interval poll? Because the underlying
 * pipeline-stats hook ALREADY polls every 30 s — duplicating that here
 * would spam toasts on every refresh. The rising-edge filter ensures
 * exactly ONE toast per state change.
 *
 * Mount discipline: this hook is consumed by the top-level
 * `PipelineHealthBanner` component, which itself mounts exactly once
 * in the Sidebar. That guarantees we don't fire duplicate toasts from
 * multiple component instances.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { diffAnomalyEdges } from "@/lib/pipelineAnomalyDiff";

const SHORT_HINT = {
  credentials_missing: "Re-verify Modal credentials from the banner tooltip.",
  failures_exceed: "Review the last 24 h job log for clustering failures.",
};

export default function useAnomalyToasts(anomalyReasons) {
  // `prevCodes` keeps the SET of reason codes we last saw — rising edges
  // are computed by subtracting it from the current set.
  const prevCodes = useRef(null);
  // Keep a code→message lookup so the falling-edge toast can quote what
  // exactly was resolved (better UX than printing the raw reason code).
  const reasonsByCode = useRef(new Map());

  useEffect(() => {
    const current = new Set((anomalyReasons || []).map((r) => r.code));
    for (const r of anomalyReasons || []) reasonsByCode.current.set(r.code, r);

    // First poll after mount: seed the baseline silently. We don't toast
    // on initial mount because the user just opened the page — they don't
    // want a stale alert from a state that was already true.
    if (prevCodes.current === null) {
      prevCodes.current = current;
      return;
    }

    const { rising, falling } = diffAnomalyEdges(prevCodes.current, current);

    for (const code of rising) {
      const reason = (anomalyReasons || []).find((r) => r.code === code);
      const msg = reason?.message || `${code} anomaly detected`;
      toast.error(`Pipeline anomaly: ${msg}`, {
        description: SHORT_HINT[code],
        duration: 8_000,
        id: `pipeline-anomaly-${code}`, // dedupe across re-renders
      });
    }
    for (const code of falling) {
      toast.success("Pipeline recovered", {
        description: `Resolved: ${code}`,
        duration: 5_000,
        id: `pipeline-recovered-${code}`,
      });
    }

    prevCodes.current = current;
  }, [anomalyReasons]);
}
